import { type SkillPick } from "encamp-shared/src/skills/skillPickContext";
import { SKILL_SLUG_PATTERN } from "encamp-shared/src/skills/slug";

/**
 * Detect an in-progress `/query` token ending at the caret (start of line or
 * after whitespace). Returns null when slash autocomplete should stay closed.
 */
export function getScoutSlashQuery(
  value: string,
  cursorPos: number
): { start: number; query: string } | null {
  const before = value.slice(0, cursorPos);
  const match = before.match(/(^|\s)\/([^\s]*)$/);
  if (!match) {
    return null;
  }
  const query = match[2] ?? "";
  const start = before.length - query.length - 1;
  return { start, query };
}

/** The slash query at a field's caret; null while a range is selected or no `/token` is there. */
export function slashQueryAtCaret(field: {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}): { start: number; query: string } | null {
  if (field.selectionStart !== field.selectionEnd) {
    return null;
  }
  return getScoutSlashQuery(field.value, field.selectionStart);
}

/** Kebab-case skill slug token: `/my-skill` at a word boundary after start/whitespace. */
const SKILL_SLUG_TOKEN_RE = new RegExp(`(^|\\s)(/${SKILL_SLUG_PATTERN})\\b`, "g");

/**
 * Extract skill slug tokens from message content (without the leading `/`).
 * Order is first-seen; duplicates are dropped.
 */
export function parseSkillSlugsFromContent(content: string): string[] {
  const slugs: string[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(SKILL_SLUG_TOKEN_RE)) {
    const token = match[2];
    if (!token) {
      continue;
    }
    const slug = token.slice(1);
    if (seen.has(slug)) {
      continue;
    }
    seen.add(slug);
    slugs.push(slug);
  }
  return slugs;
}

type ScoutSkillSlugTextPart =
  | { type: "text"; value: string }
  | { type: "skillSlug"; value: string };

/** Split content into plain text and `/slug` tokens for highlight rendering. */
export function splitContentWithSkillSlugs(
  content: string,
  /** When set, only these slugs (no leading `/`) become highlight tokens. */
  knownSlugs?: ReadonlySet<string>
): ScoutSkillSlugTextPart[] {
  const parts: ScoutSkillSlugTextPart[] = [];
  let lastIndex = 0;
  for (const match of content.matchAll(SKILL_SLUG_TOKEN_RE)) {
    const token = match[2];
    const matchIndex = match.index;
    if (token === undefined || matchIndex === undefined) {
      continue;
    }
    const slug = token.slice(1);
    if (knownSlugs && !knownSlugs.has(slug)) {
      continue;
    }
    const tokenStart = matchIndex + (match[1]?.length ?? 0);
    if (tokenStart > lastIndex) {
      parts.push({ type: "text", value: content.slice(lastIndex, tokenStart) });
    }
    parts.push({ type: "skillSlug", value: token });
    lastIndex = tokenStart + token.length;
  }
  if (lastIndex < content.length) {
    parts.push({ type: "text", value: content.slice(lastIndex) });
  }
  return parts;
}

/** True when content includes at least one `/slug` from the known catalog. */
export function contentHasKnownSkillSlug(
  content: string,
  knownSlugs: ReadonlySet<string>
): boolean {
  if (knownSlugs.size === 0 || content.length === 0) {
    return false;
  }
  for (const slug of parseSkillSlugsFromContent(content)) {
    if (knownSlugs.has(slug)) {
      return true;
    }
  }
  return false;
}

/** The menu picks (slug -> skill id) for slugs that appear in `content`, in message order. */
export function skillPicksInContent(
  picks: ReadonlyMap<string, string>,
  content: string
): SkillPick[] {
  return parseSkillSlugsFromContent(content).flatMap((slug) => {
    const id = picks.get(slug);
    return id ? [{ slug, id }] : [];
  });
}

/** Merge optional page-context sections; empty sections are dropped. */
export function mergeScoutPageContext(
  ...sections: Array<string | null | undefined>
): string | undefined {
  const merged = sections
    .map((section) => section?.trim())
    .filter((section): section is string => Boolean(section))
    .join("\n\n");
  return merged.length > 0 ? merged : undefined;
}
